import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { mainApi } from '@/lib/api';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { StripeAccountConnect } from '@/features/auth/components/StripeAccountConnect';
import { PayPalAccountConnect } from '@/features/auth/components/PayPalAccountConnect';
import { AdyenAccountConnect } from '@/features/auth/components/AdyenAccountConnect';
import { useUser } from '@/queries/useUser';

export const IntegrationAuthorizationPage = () => {
    const [searchParams] = useSearchParams();
    const { user } = useUser();
    const [showSpinner, setShowSpinner] = useState(false);

    const connect = searchParams.get('connect');
    const closeWindow = searchParams.get('closeWindow');

    useEffect(() => {
        // If closeWindow=true, close the popup
        if (closeWindow === 'true') {
            // Check for delayed close (Stripe)
            if (localStorage.getItem('stripe-connect-started') === 'True') {
                setShowSpinner(true);
                localStorage.setItem('stripe-connect-started', 'False');
                setTimeout(() => {
                    window.close();
                }, 2000);
            } else {
                window.close();
            }
        }
    }, [closeWindow]);

    const handleStripeConnect = async (country: string) => {
        const integrationHandler = connect === 'stripe/standard' ? 'stripe/standard' : 'stripe/connect';

        try {
            // Return URL should point back to this page with closeWindow=true to close the popup
            const returnUrl = window.location.origin + '/integration-authorization?closeWindow=true';
            const response = await mainApi.post(`v1/integrations/${integrationHandler}`, {
                country,
                returnUrl,
            });

            // Track analytics
            const analyticsEvent =
                integrationHandler === 'stripe/standard' ? 'stripe-connect-standard-started' : 'stripe-connect-started';

            stanAnalytics(analyticsEvent, {
                meta: { user_id: user?.user_id, username: user?.username },
            });

            // Redirect to Stripe OAuth
            if (response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error('Stripe connection error:', error);
        }
    };

    const handlePayPalConnect = async () => {
        try {
            // Return URL should point back to this page with closeWindow=true to close the popup
            const returnUrl = window.location.origin + '/integration-authorization?closeWindow=true';
            const response = await mainApi.post('v1/integrations/paypal', { returnUrl });

            stanAnalytics('paypal-connect-started', {
                meta: { user_id: user?.user_id, username: user?.username },
            });

            // Redirect to PayPal OAuth
            if (response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error('PayPal connection error:', error);
        }
    };

    const handleAdyenConnect = async (country: string) => {
        try {
            // Return URL should point back to this page with closeWindow=true to close the popup
            const returnUrl = window.location.origin + '/integration-authorization?closeWindow=true';
            const response = await mainApi.post('v1/integrations/adyen', {
                country,
                returnUrl,
            });

            stanAnalytics('adyen-connect-started', {
                meta: { user_id: user?.user_id, username: user?.username },
            });

            // Redirect to Adyen OAuth
            if (response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error('Adyen connection error:', error);
        }
    };

    // Show loading spinner when closing window
    if (showSpinner) {
        return (
            <div className='flex h-screen items-center justify-center'>
                <div className='text-center'>
                    <h5 className='mb-4 font-bold'>Updating Stripe Information</h5>
                    <div className='border-brand-main mx-auto size-12 animate-spin rounded-full border-b-2' />
                </div>
            </div>
        );
    }

    // Show appropriate connection UI
    if (connect === 'stripe' || connect === 'stripe/standard') {
        return <StripeAccountConnect onConnect={handleStripeConnect} standard={connect === 'stripe/standard'} />;
    }

    if (connect === 'paypal') {
        return <PayPalAccountConnect onConnect={handlePayPalConnect} />;
    }

    if (connect === 'adyen') {
        return <AdyenAccountConnect onConnect={handleAdyenConnect} />;
    }

    // Default loading state
    return (
        <div className='flex h-screen items-center justify-center'>
            <div className='text-center'>
                <div className='border-brand-main mb-4 size-12 animate-spin rounded-full border-b-2' />
                <p className='text-body-1-regular'>Loading...</p>
            </div>
        </div>
    );
};

export default IntegrationAuthorizationPage;
